import { useCallback, useEffect, useRef, useState } from "preact/hooks"

import { revealTimes } from "@workspace/contracts/embed/reveal"
import type {
  ChatTrigger,
  EmbedKind,
  WidgetPayload,
} from "@workspace/contracts/embed"

import {
  ApiError,
  endSession,
  fetchTranscript,
  sendTurn,
  startSession,
  type Api,
} from "../api"
import { uuidv7 } from "../events"
import type { PageContext } from "../host"
import { clearSession, loadSession, saveSession } from "./session-store"

/**
 * One visitor's conversation with a surface's agent - the runtime twin of
 * the dashboard console (`agent-chat.tsx` + `use-reply-reveal.ts`), without
 * `useChat`: the session is minted on the FIRST send (no row for someone
 * who only looks), each turn is `POST /api/embed/chat`, and the reply -
 * which arrives whole - is metered out on the contract's cadence
 * (`REPLY_REVEAL`): dots while the request is out, the first paragraph
 * after the hold, dots again, the next. Stop cancels the request; Retry
 * re-sends the same turn as `regenerate-message`, which the server answers
 * afresh without storing the question twice.
 *
 * The session outlives the page: once minted it is kept per surface
 * (`session-store.ts`), and a surface that mounts with one kept reads the
 * thread back (`GET /api/embed/sessions/current`) so a reload, or the next
 * page of the site, shows the conversation where it was. A kept session
 * the server refuses (expired, ended elsewhere) is forgotten and the next
 * send opens afresh - the same path a refused send already took.
 *
 * Stopping is the visitor's, with a confirmation first: `askToStop` puts
 * the question in the thread as the agent's card, `answerStop(true)` ends
 * the conversation on the server (`POST /api/embed/sessions/current/end`)
 * and clears the page, so the next word starts a new one; `false` takes
 * the card away. While the card is up nothing sends.
 */

export interface Turn {
  id: string
  role: "user" | "assistant"
  /** The turn's paragraphs - one for a visitor, one per paragraph of a reply. */
  parts: string[]
  /** How many of `parts` are on screen; a visitor's turn shows whole. */
  shown: number
}

/** The stop card while it is up: the request out, or what refused it. */
export interface StopPrompt {
  busy: boolean
  error: string | null
}

export interface Conversation {
  turns: Turn[]
  /** A request is out, or a reply is still being revealed - the dots show. */
  busy: boolean
  /** What went wrong with the last turn, as a sentence, with Retry beside it. */
  error: string | null
  /** The conversation's id once a session exists - for the events it is about. */
  sessionId: string | null
  /** The stop card, while the visitor is being asked. */
  stopping: StopPrompt | null
  send: (text: string) => void
  retry: () => void
  stop: () => void
  /** Put the "Stop the conversation?" card in the thread. */
  askToStop: () => void
  /** The card's answer: end the conversation, or keep chatting. */
  answerStop: (accept: boolean) => void
}

interface Session {
  token: string
  conversationId: string
}

/** A stored turn as the page shows it - whole, its paragraphs all on screen. */
function turnOf(message: {
  id: string
  role: "user" | "assistant"
  parts: { text: string }[]
}): Turn | null {
  const parts = message.parts
    .map((part) => part.text)
    .filter((text) => text.trim())
  if (parts.length === 0) return null
  return { id: message.id, role: message.role, parts, shown: parts.length }
}

export function useConversation(
  api: Api,
  payload: WidgetPayload,
  embed: { kind: EmbedKind; id: string },
  page: PageContext
): Conversation {
  const [turns, setTurns] = useState<Turn[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [stopping, setStopping] = useState<StopPrompt | null>(null)

  const session = useRef<Session | null>(null)
  const controller = useRef<AbortController | null>(null)
  const timers = useRef<number[]>([])
  const lastSent = useRef<{ id: string; text: string } | null>(null)

  const clearTimers = () => {
    for (const timer of timers.current) window.clearTimeout(timer)
    timers.current = []
  }
  useEffect(() => () => clearTimers(), [])

  // The session is kept under the surface's id: in a group each member
  // talks to its own agent, so each keeps its own thread.
  const widgetId = payload.id

  /** Drop the session here and in storage; the next send opens afresh. */
  const forget = useCallback(() => {
    session.current = null
    setSessionId(null)
    clearSession(widgetId)
  }, [widgetId])

  // A kept session: the token is in hand at once (a send before the read
  // returns still goes to the same thread), the turns follow. A thread the
  // server says has ended, or refuses, is forgotten; a network fault keeps
  // the session and shows the welcome alone - the thread is still there.
  useEffect(() => {
    const stored = loadSession(widgetId)
    if (!stored) return
    let cancelled = false
    session.current = {
      token: stored.token,
      conversationId: stored.conversationId,
    }
    setSessionId(stored.conversationId)
    fetchTranscript(api, stored.token)
      .then((transcript) => {
        if (cancelled) return
        if (transcript.ended) {
          forget()
          return
        }
        setTurns(
          transcript.messages.flatMap((message) => {
            const turn = turnOf(message)
            return turn ? [turn] : []
          })
        )
      })
      .catch((caught: unknown) => {
        if (cancelled) return
        if (caught instanceof ApiError && caught.status < 500) forget()
      })
    return () => {
      cancelled = true
    }
  }, [api, widgetId, forget])

  const ensureSession = useCallback(async (): Promise<Session> => {
    if (session.current) return session.current
    const opened = await startSession(api, {
      kind: embed.kind,
      id: embed.id,
      widgetId: embed.kind === "group" ? payload.id : undefined,
      landingUrl: page.href,
      referrer: page.referrer || undefined,
      utm: Object.keys(page.utm).length ? page.utm : undefined,
    })
    session.current = {
      token: opened.token,
      conversationId: opened.conversationId,
    }
    saveSession(widgetId, {
      token: opened.token,
      conversationId: opened.conversationId,
      expiresAt: opened.expiresAt,
    })
    setSessionId(opened.conversationId)
    return session.current
  }, [api, embed.kind, embed.id, payload.id, page, widgetId])

  const run = useCallback(
    async (message: { id: string; text: string }, trigger: ChatTrigger) => {
      controller.current?.abort()
      const abort = new AbortController()
      controller.current = abort
      clearTimers()
      setBusy(true)
      setError(null)
      try {
        const { token } = await ensureSession()
        const reply = await sendTurn(api, token, message, trigger, abort.signal)
        if (abort.signal.aborted) return
        const parts = reply.parts.filter((part) => part.trim())
        if (parts.length === 0) {
          throw new Error("The agent did not answer. Try again.")
        }
        // Shown from zero, so the arrival itself never paints; each timer
        // reveals one more paragraph, the last one puts the dots away.
        setTurns((prev) => [
          ...prev,
          { id: reply.id, role: "assistant", parts, shown: 0 },
        ])
        timers.current = revealTimes(parts.length).map((at, index) =>
          window.setTimeout(() => {
            setTurns((prev) =>
              prev.map((turn) =>
                turn.id === reply.id ? { ...turn, shown: index + 1 } : turn
              )
            )
            if (index === parts.length - 1) setBusy(false)
          }, at)
        )
      } catch (caught) {
        if (abort.signal.aborted) return
        // A session the server refused (expired, ended) is minted anew on
        // the next send rather than sent again.
        if (
          caught instanceof ApiError &&
          (caught.status === 401 || caught.status === 409)
        ) {
          forget()
        }
        setBusy(false)
        // The server's words, or ours; a browser's ("Failed to fetch") are
        // for nobody, so a network fault gets a line of its own.
        setError(
          caught instanceof ApiError
            ? caught.message
            : caught instanceof TypeError
              ? "Could not reach the agent. Try again."
              : caught instanceof Error && caught.message
                ? caught.message
                : "Something went wrong. Try again."
        )
      }
    },
    [api, ensureSession, forget]
  )

  const send = useCallback(
    (raw: string) => {
      const text = raw.trim().slice(0, 4000)
      if (!text || busy || stopping) return
      const message = { id: uuidv7(), text }
      lastSent.current = message
      setTurns((prev) => [
        ...prev,
        { id: message.id, role: "user", parts: [text], shown: 1 },
      ])
      void run(message, "submit-message")
    },
    [busy, stopping, run]
  )

  const retry = useCallback(() => {
    if (!lastSent.current || busy || stopping) return
    void run(lastSent.current, "regenerate-message")
  }, [busy, stopping, run])

  const stop = useCallback(() => {
    controller.current?.abort()
    controller.current = null
    clearTimers()
    // A reply cut mid-reveal shows whole: it is in hand, the wait is over.
    setTurns((prev) =>
      prev.map((turn) =>
        turn.role === "assistant" && turn.shown < turn.parts.length
          ? { ...turn, shown: turn.parts.length }
          : turn
      )
    )
    setBusy(false)
  }, [])

  const askToStop = useCallback(() => {
    // Nothing to stop before the first send, and not over a reply in flight.
    if (!session.current || busy) return
    setStopping({ busy: false, error: null })
  }, [busy])

  const answerStop = useCallback(
    async (accept: boolean) => {
      const current = session.current
      if (!accept || !current) {
        setStopping(null)
        return
      }
      setStopping({ busy: true, error: null })
      try {
        await endSession(api, current.token)
      } catch (caught) {
        // A session the server no longer knows is as ended as it gets.
        const gone =
          caught instanceof ApiError &&
          (caught.status === 401 || caught.status === 409)
        if (!gone) {
          setStopping({
            busy: false,
            error:
              caught instanceof ApiError
                ? caught.message
                : "Could not stop the conversation. Try again.",
          })
          return
        }
      }
      // Ended: the thread leaves the page with the session, and the next
      // word opens a new conversation - the panel stays where it is.
      forget()
      lastSent.current = null
      setTurns([])
      setError(null)
      setStopping(null)
    },
    [api, forget]
  )

  return {
    turns,
    busy,
    error,
    sessionId,
    stopping,
    send,
    retry,
    stop,
    askToStop,
    answerStop,
  }
}
